import {
  ABBREVIATED_FULFILLMENT_METHODS,
  FULFILLMENT_METHODS,
  MA,
  MULTI_SELECT_QUANTITY
} from './constants';
import {
  createPriorityOfFulfillments,
  getCurrentlyAvailable,
  getFulfillmentLocation,
  getFulfillmentMethodFromCart,
  updateAvailableFulfillments,
} from './promo-fulfillment-utils';

/**
 * @typedef {Object} CartRequest
 * @property {Array<object>} itemDetails - The itemDetails
 * @property<optional> {string} localStoreId - The localStoreId
 */

/**
 * @typedef {Object} CartRequestWrapper
 * @property {CartRequest} CartRequest - The CartRequest
 */

/**
 * @typedef {Object} CartRequestBuilderOptions
 * @property {Object} product - The product model
 * @property {Object} promoItemModel - The mcc-cart product itemModel
 * @property {number} desiredQuantity - The desired ***final*** quantity
 * @property {string} storeId - The store object
 * @property {string} selectedFulfillment - The preferred fulfillment method
 */

export const emptyPromoCart = {
  bogoItemInCart: null,
  currentPromoTier: 0,
  isPromoAchieved: false,
  progressMade: 0,
  promoCartItemsObj: {},
  promoCartQuantity: 0,
  promoCartValue: 0,
  firstListCartItems: [],
  firstListCartItemIds: [],
  firstListCartValue: 0,
  firstListCartQuantity: 0,
  secondListCartItems: [],
  secondListCartItemIds: [],
  secondListCartValue: 0,
  secondListCartQuantity: 0,
  promoSuccessLevel: '',
  hasAnchorItemInCart: false,
  secondListItemInCart: false
};

const cartIsEmpty = (rawCartModel) => rawCartModel?.messagesModel?.[0]?.shortDescription === 'Empty Shopping Cart';
export const isValidRawCart = (CartModel) => {
  return !!CartModel && (!!CartModel.itemModels || cartIsEmpty(CartModel));
};

/**
 *  TODO: need to differentiate fetch from update
 * fetch can successfully return itemModels alongside an errorModel
 * some of the itemModels are good, others may be associated with the cart error
 * errorModel messages will have a correlationId to associate it with an itemModel
 * what do we do with this info? so many edge cases. sigh...
 * Seems that any update request for a single item, if it has an error, it did not change quantity in cart
 * need to check above for BOGO multi add though
 */
const cartResponseHandler = (rawCartResponse) => {
  const CartModel = rawCartResponse?.CartModel;

  if (!isValidRawCart(CartModel)) {
    const cartError = CartModel?.errorModel?.[0] || {};
    const errorMessage = new Error(`cart response contains error: ${cartError.errorCode} ${cartError.description}`);

    return Promise.reject(errorMessage);
  }

  return Promise.resolve(CartModel);
};

export const cartFunctions = {
  fetchCartHelper: () => {
    if (typeof window === 'undefined') return Promise.reject();

    return window.THDCart.getCartData()
      .then(cartResponseHandler);
  },
  updateCartHelper: (CartRequest) => {
    if (typeof window === 'undefined') return Promise.reject();

    return window.THDCart.updateCartData({ CartRequest })
      .then(cartResponseHandler);
  },
  addToCartHelper: (CartRequest) => {
    if (typeof window === 'undefined') return Promise.reject();

    return window.THDCart.addToCart(CartRequest)
      .then(cartResponseHandler);
  },
};

const getHost = () => {
  let host = 'https://www.homedepot.com';
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    host = origin.match(/local/g) ? 'https://www.homedepot.com' : origin;
  }
  return host;
};

const generateAddToCartItemDetail = ({
  itemId, fulfillmentMethod, storeId, quantity, displayPosition, applianceDeliveryStore, deliveryZip
}) => {
  const fulfillmentLocation = getFulfillmentLocation({ fulfillmentMethod, storeId, deliveryZip });

  return {
    fulfillmentMethod,
    fulfillmentLocation,
    quantity,
    itemId,
    displayPosition,
    applianceDeliveryStore,
  };
};

export const buildAnalyticsCartEventObject = (cartRequest, cartResponse) => {
  const { localStoreId: storeId } = cartRequest.CartRequest;
  const cartEvent = {
    cartId: cartResponse.orderId || '',
    items: cartRequest.CartRequest.itemDetails.map(({ itemId, quantity, fulfillmentMethod }) => {
      return ({
        productInfo: {
          sku: itemId,
        },
        quantity,
        price: {
          basePrice: (cartResponse?.itemModels || []).find((item) => item.itemId === itemId)?.unitPrice || '',
        },
        fulfillment: {
          method: ABBREVIATED_FULFILLMENT_METHODS[fulfillmentMethod],
          store: fulfillmentMethod === FULFILLMENT_METHODS.BOPIS ? storeId : '',
          zip: '',
        }
      });
    })
  };
  return cartEvent;
};

const buildApplianceItemAddToCartRequest = ({
  itemId, displayPosition, applianceDeliveryStore, deliveryZip
}) => {
  return {
    CartRequest: {
      itemDetails: [generateAddToCartItemDetail({
        itemId,
        fulfillmentMethod: FULFILLMENT_METHODS.APPLIANCE,
        quantity: 1,
        displayPosition,
        applianceDeliveryStore,
        deliveryZip
      })],
    }
  };
};

/**
 * Builds a CartRequest for mcc-cart
 * @param {CartRequestBuilderOptions} options - CartRequest builder options
 * @returns {CartRequestWrapper} an object containing CartRequest
 */
export const buildAddToCartRequest = ({
  applianceDeliveryStore: applDeliveryStoreParam,
  deliveryZip,
  desiredQuantity = 1,
  product,
  promoItemModel,
  selectedFulfillment = '',
  storeId,
}) => {
  const applianceDeliveryStore = applDeliveryStoreParam || storeId;
  const { itemId, displayPosition = '' } = product;
  const fulfillmentPriorities = createPriorityOfFulfillments(selectedFulfillment);
  const currentlyAvailable = getCurrentlyAvailable(promoItemModel, product);
  let neededToFulfill = desiredQuantity;

  if (product.identifiers.productType === MA) {
    return buildApplianceItemAddToCartRequest({ itemId, displayPosition, applianceDeliveryStore, deliveryZip });
  }

  const itemDetails = fulfillmentPriorities
    .reduce((items, fulfillmentMethod) => {
      const availableQty = currentlyAvailable[fulfillmentMethod] || 0;
      const quantity = Math.min(neededToFulfill, availableQty);

      if (quantity > 0) {
        neededToFulfill -= quantity;
        items.push(generateAddToCartItemDetail({
          deliveryZip,
          displayPosition,
          fulfillmentMethod,
          itemId,
          quantity,
          storeId,
        }));
      }

      return items;
    }, []);

  return { CartRequest: { itemDetails, localStoreId: storeId } };
};

export const buildAddToCartOverlayOptions = (channel, overrides = {}) => {
  return {
    channel,
    hidden: false,
    misship: false,
    paypal: false,
    host: getHost(),
    ...overrides
  };
};

export const buildUpdateCartRequest = ({
  deliveryZip,
  desiredQuantity,
  product,
  promoItemModel,
  selectedFulfillment = '',
  storeId,
}) => {
  let itemDetails = [];
  let neededToFulfill = desiredQuantity - (promoItemModel?.quantity || 0);
  const fulfillmentPriorities = createPriorityOfFulfillments(selectedFulfillment);

  if (neededToFulfill > 0) {
    let currentlyAvailable = getCurrentlyAvailable(promoItemModel, product);

    // Updating or adding quantities to cart
    itemDetails = fulfillmentPriorities.reduce((items, fulfillmentMethod) => {
      const availableQty = currentlyAvailable[fulfillmentMethod];
      if (!availableQty || neededToFulfill <= 0) return items;

      // Updating item
      const lineItem = promoItemModel.fulfillments[fulfillmentMethod];
      if (lineItem && lineItem.id && lineItem.quantity) {
        const qtyChange = Math.min(neededToFulfill, availableQty);
        neededToFulfill -= qtyChange;
        currentlyAvailable = updateAvailableFulfillments(fulfillmentMethod, currentlyAvailable, qtyChange);

        items.push({
          itemId: promoItemModel.itemId,
          quantity: qtyChange + lineItem.quantity,
          lineItemId: lineItem.id,
          fulfillmentMethod,
          fulfillmentLocation: getFulfillmentLocation({ fulfillmentMethod, storeId, deliveryZip })
        });
      }

      return items;
    }, []);

  } else if (neededToFulfill < 0) {

    if (product.identifiers.productType === MA) {
      itemDetails = [{
        itemId: promoItemModel.itemId,
        quantity: 0,
        lineItemId: promoItemModel.fulfillments.DirectDelivery.id,
        fulfillmentMethod: 'DirectDelivery',
        fulfillmentLocation: getFulfillmentLocation({ fulfillmentMethod: 'DirectDelivery', storeId, deliveryZip }),
        applianceDeliveryStore: promoItemModel.applianceDeliveryStore,
      }];
    } else {
      // reduceRight will subtract values from low-pri to high-pri fulfillments
      itemDetails = fulfillmentPriorities.reduceRight((items, fulfillmentMethod) => {
        if (neededToFulfill >= 0) return items;

        const lineItem = promoItemModel.fulfillments[fulfillmentMethod];
        if (lineItem && lineItem.id && lineItem.quantity) {
          const quantity = Math.max(neededToFulfill + lineItem.quantity, 0);

          if (quantity < lineItem.quantity) {
            neededToFulfill -= quantity - lineItem.quantity;
            items.push({
              itemId: promoItemModel.itemId,
              quantity,
              lineItemId: lineItem.id,
              fulfillmentMethod,
              fulfillmentLocation: getFulfillmentLocation({ fulfillmentMethod, storeId, deliveryZip }),
              applianceDeliveryStore: promoItemModel.applianceDeliveryStore
            });
          }
        }

        return items;
      }, []);
    }
  }

  return { CartRequest: { itemDetails, localStoreId: storeId } };
};

const getPromoSuccessLevel = ({
  isForwardsBogo,
  isBackwardsBogo,
  isForwardsB1gy,
  isMsb,
  isBmsm,
  bogoItemInCart,
  hasAnchorItemInCart,
  bogoSelectedProduct,
  firstListCartQuantity,
  secondListCartQuantity,
  src1EligibilityCriteria,
  tgt1EligibilityCriteria,
  promoCartValue,
  promoCartQuantity,
  progressMade,
  minPurchaseQuantity,
  minPurchaseAmount,
}) => {
  const qualifyingThresholdMet = firstListCartQuantity >= src1EligibilityCriteria.minPurchaseQuantity;
  const rewardThresholdMet = secondListCartQuantity >= tgt1EligibilityCriteria.minPurchaseQuantity;
  const underRewardThresholdMet = secondListCartQuantity < tgt1EligibilityCriteria.minPurchaseQuantity;
  const underQualifyingThresholdMet = firstListCartQuantity < src1EligibilityCriteria.minPurchaseQuantity;

  if (isForwardsB1gy) {
    if (qualifyingThresholdMet && rewardThresholdMet) {
      return 'success';
    }
    if (firstListCartQuantity === 0 && secondListCartQuantity === 0) {
      return 'initial';
    } if (qualifyingThresholdMet || underRewardThresholdMet || underQualifyingThresholdMet || rewardThresholdMet) {
      return 'almost';
    }
  }

  if (isForwardsBogo || isBackwardsBogo) {
    if (bogoItemInCart && hasAnchorItemInCart) return 'success';
    if (bogoSelectedProduct) return 'almost';
  }

  if (isMsb) {
    if (!promoCartQuantity || !promoCartValue) return '';
    let amountLeft;
    let percentLeft;
    if (minPurchaseQuantity) {
      amountLeft = Math.max(0, minPurchaseQuantity - promoCartQuantity);
      percentLeft = Math.max(0, Math.floor(100 - (promoCartQuantity / minPurchaseQuantity) * 100));
    } else { // minPurchaseAmount
      amountLeft = Math.max(0, minPurchaseAmount - promoCartValue);
      percentLeft = Math.max(0, Math.floor(100 - (promoCartValue / minPurchaseAmount) * 100));
    }
    if (amountLeft === 0 || percentLeft === 0) return 'success';
    if (amountLeft === 1 || percentLeft <= 10) return 'almost';

  }

  if (isBmsm) {
    if (progressMade >= 100) {
      return 'success';
    }
    if (promoCartQuantity) {
      return 'almost';
    }
  }

  return '';
};

const cartItemIsSanitary = (cartItem) => {
  return cartItem
    && cartItem.itemId
    && typeof cartItem.lineItemId === 'string'
    && cartItem.lineItemId.length
    && Number(cartItem.quantity)
    && Number(cartItem.totalItemPrice)
    && Array.isArray(cartItem.fulfillmentModel)
    && cartItem.fulfillmentModel[0]
    && cartItem.fulfillmentModel[0].fulfillmentMethod;
};

export const generatePromoCartItemsObj = ({ itemModels, allListProductsAndAnchor }) => {
  const promoCartItemsObj = {};

  if (!Array.isArray(itemModels)) return promoCartItemsObj;

  itemModels
    .forEach((promoCartItemEntry) => {
      const image = promoCartItemEntry.image;
      const itemId = promoCartItemEntry.itemId;
      const quantity = Number(promoCartItemEntry.quantity);
      const actualItemPrice = Number(allListProductsAndAnchor.get(itemId)?.pricing?.value);
      // grab pricing from product
      // if pricing is unavailable, use default mechanism for calculation
      // TODO: eventually remove fallback
      const totalItemPrice = actualItemPrice
        ? Number(actualItemPrice * quantity)
        : Number(promoCartItemEntry.totalItemPrice);
      const id = promoCartItemEntry.lineItemId;
      const applianceDeliveryStore = promoCartItemEntry.applianceDeliveryStore || '';
      const newFulfillmentMethod = getFulfillmentMethodFromCart(promoCartItemEntry);

      if (!promoCartItemsObj[itemId]) {
        promoCartItemsObj[itemId] = {
          itemId,
          image,
          quantity,
          totalItemPrice,
          applianceDeliveryStore,
          fulfillments: {
            [newFulfillmentMethod]: {
              quantity,
              id
            }
          }
        };
      } else {
        promoCartItemsObj[itemId].quantity += quantity;
        promoCartItemsObj[itemId].totalItemPrice += totalItemPrice;
        promoCartItemsObj[itemId].fulfillments[newFulfillmentMethod] = { quantity, id };
        promoCartItemsObj[itemId].applianceDeliveryStore = applianceDeliveryStore;
      }
    });

  return promoCartItemsObj;
};

// Gets the first tier that hasn't been completed for bmsm. If all tiers have been completed returns the last tier.
// Returns 0 for non bmsm promotions
// Tiers are 0 based
const getCurrentPromoTier = (isBmsm, tiers, promoCartValue, promoCartQuantity) => {
  if (!isBmsm) return 0;

  const currentIndex = tiers.findIndex(({ minPurchaseAmount, minPurchaseQuantity }) => {
    if (minPurchaseAmount) return promoCartValue < minPurchaseAmount;
    if (minPurchaseQuantity) return promoCartQuantity < minPurchaseQuantity;
    return false;
  });

  return currentIndex === -1 ? tiers.length - 1 : currentIndex;
};

const getProgressMade = ({ promoCartQuantity, promoCartValue, minPurchaseAmount, minPurchaseQuantity }) => {
  const progressMadeQuantity = Math.floor((promoCartQuantity / minPurchaseQuantity) * 100) || 0;
  const progressMadeAmount = Math.floor((promoCartValue / minPurchaseAmount) * 100) || 0;
  const progressMade = Math.min(100, minPurchaseQuantity ? progressMadeQuantity : progressMadeAmount);

  return progressMade;
};

// Returns progress made into the current tier.
const getMultiTierPromoProgress = ({ promoCartValue, promoCartQuantity, rewardTiers, currentPromoTier }) => {
  const {
    minPurchaseAmount: currentTierMinAmount,
    minPurchaseQuantity: currentTierMinQuantity
  } = rewardTiers[currentPromoTier];

  if (currentPromoTier === 0) {
    return getProgressMade({
      promoCartValue,
      promoCartQuantity,
      minPurchaseAmount: currentTierMinAmount,
      minPurchaseQuantity: currentTierMinQuantity
    });
  }

  const {
    minPurchaseAmount: previousTierMinAmount,
    minPurchaseQuantity: previousTierMinQuantity
  } = rewardTiers[currentPromoTier - 1];

  // Progress made from previous tiers need to be removed in order to calculate current tier progress
  const minPurchaseAmount = currentTierMinAmount - previousTierMinAmount;
  const minPurchaseQuantity = currentTierMinQuantity - previousTierMinQuantity;

  const currentTierQuantity = promoCartQuantity - previousTierMinQuantity;
  const currentTierValue = promoCartValue - previousTierMinAmount;

  const progressMade = getProgressMade({
    promoCartQuantity: currentTierQuantity,
    promoCartValue: currentTierValue,
    minPurchaseQuantity,
    minPurchaseAmount,
  });

  return progressMade;
};

const getPromotionProgress = ({
  currentPromoTier,
  minPurchaseAmount,
  minPurchaseQuantity,
  promoCartQuantity,
  promoCartValue,
  rewardTiers,
}) => {
  const progressMade = rewardTiers.length > 1
    ? getMultiTierPromoProgress({ promoCartValue, promoCartQuantity, rewardTiers, currentPromoTier })
    : getProgressMade({ promoCartValue, promoCartQuantity, minPurchaseAmount, minPurchaseQuantity });

  const isPromoAchieved = progressMade === 100;
  return [progressMade, isPromoAchieved];
};

// returns list of storeSkus that are in cart but not known to be in the promotion
export const getCartStoreSkusWithoutPromoItems = (cartModel, allListProductsAndAnchor) => {
  return (cartModel?.itemModels || [])
    .filter((cartItem) => !allListProductsAndAnchor.has(cartItem.itemId))
    .map((cartItem) => cartItem.storeSku);
};

// returns list of cartItems that are eligible for promotion
const getCartItemsInPromotion = ({ cartItemsPromotionValidity, CartModel, allListProductsAndAnchor }) => {
  // Items in cart that are part of promotion and included in batch call
  const itemModels = Array.isArray(CartModel?.itemModels) ? CartModel.itemModels : [];
  const itemsInCartAndDrawer = itemModels.filter((cartItem) => allListProductsAndAnchor.has(cartItem.itemId));

  // Items in cart that are part of promotion, but not included in batch call
  const itemsInCartAndNotInDrawer = cartItemsPromotionValidity
    .filter(({ eligible }) => eligible)
    .map(({ storeSkuId }) => {
      return CartModel.itemModels.find((item) => item.storeSku === storeSkuId);
    });

  return [...itemsInCartAndDrawer, ...itemsInCartAndNotInDrawer].filter(cartItemIsSanitary);
};

export const transformCartModelToPromoCart = ({
  rawCartModel = {},
  selectedItemsModel = [],
  isForwardsB1gy = false,
  isBackwardsBogo = false,
  isForwardsBogo = false,
  isMsb = false,
  isBmsm = false,
  isDollarThresholdBogo = false,
  displayableFirstListItemIds = [],
  displayableSecondListItemIds = [],
  anchorItemId = '',
  bogoSelectedProduct = '',
  rewardTiers = [],
  src1EligibilityCriteria,
  tgt1EligibilityCriteria,
  src1MinPurchaseAmount = 0,
  allListProductsAndAnchor,
  cartItemsPromotionValidity = [],
} = {}) => {
  if (!Array.isArray(rawCartModel.itemModels) && !selectedItemsModel.length) {
    return emptyPromoCart;
  }

  // All cart items that are part of the promotion regardless if the item is part of batch call or not
  const cartItemsInPromo = getCartItemsInPromotion({
    cartItemsPromotionValidity,
    CartModel: rawCartModel,
    allListProductsAndAnchor
  });

  const promoCartItemsObj = generatePromoCartItemsObj({ itemModels: cartItemsInPromo, allListProductsAndAnchor });

  const hasAnchorItemInCart = !!promoCartItemsObj?.[anchorItemId]?.quantity;

  let firstListCartItems = [];
  let firstListCartItemIds = [];
  let firstListCartValue = 0;
  let firstListCartQuantity = 0;
  Object.values(promoCartItemsObj)
    .filter((promoItem) => displayableFirstListItemIds.includes(promoItem.itemId))
    .forEach((promoItem) => {
      firstListCartItems.push(promoItem);
      firstListCartItemIds.push(promoItem.itemId);
      firstListCartValue += promoItem.totalItemPrice;
      firstListCartQuantity += promoItem.quantity;
    });

  let secondListCartItems = [];
  let secondListCartItemIds = [];
  let secondListCartValue = 0;
  let secondListCartQuantity = 0;
  Object.values(promoCartItemsObj)
    .filter((promoItem) => displayableSecondListItemIds.includes(promoItem.itemId))
    .forEach((promoItem) => {
      secondListCartItems.push(promoItem);
      secondListCartItemIds.push(promoItem.itemId);
      secondListCartValue += promoItem.totalItemPrice;
      secondListCartQuantity += promoItem.quantity;
    });
  // Items selected but not added to cart yet
  selectedItemsModel.forEach(({ pricing }) => {
    firstListCartValue += (MULTI_SELECT_QUANTITY * Number(pricing.value));
    firstListCartQuantity += MULTI_SELECT_QUANTITY;
  });

  const currentPromoTier = getCurrentPromoTier(isBmsm, rewardTiers, firstListCartValue, firstListCartQuantity);

  const { minPurchaseAmount: tieredMinPurchaseAmount, minPurchaseQuantity } = rewardTiers[currentPromoTier] || {};
  const minPurchaseAmount = isDollarThresholdBogo ? src1MinPurchaseAmount : tieredMinPurchaseAmount;

  const [progressMade, isPromoAchieved] = getPromotionProgress({
    rewardTiers,
    currentPromoTier,
    promoCartValue: firstListCartValue,
    promoCartQuantity: firstListCartQuantity,
    minPurchaseAmount,
    minPurchaseQuantity
  });

  const nonAnchorIdInCart = displayableFirstListItemIds.find((itemId) => promoCartItemsObj[itemId]);
  const bogoItemInCart = ((isForwardsBogo || isBackwardsBogo) && nonAnchorIdInCart)
    ? promoCartItemsObj[nonAnchorIdInCart]
    : null;

  const promoSuccessLevel = getPromoSuccessLevel({
    isMsb,
    isForwardsB1gy,
    isBackwardsBogo,
    isForwardsBogo,
    isBmsm,
    bogoItemInCart,
    hasAnchorItemInCart,
    bogoSelectedProduct,
    promoCartValue: firstListCartValue,
    promoCartQuantity: firstListCartQuantity,
    firstListCartItemIds,
    secondListCartItemIds,
    firstListCartQuantity,
    secondListCartQuantity,
    progressMade,
    rewardTiers,
    minPurchaseAmount,
    minPurchaseQuantity,
    src1EligibilityCriteria,
    tgt1EligibilityCriteria,
  });

  const cartItems = rawCartModel.itemModels;
  const secondListItemInCart = cartItems?.some(
    (item) => displayableSecondListItemIds.includes(item.itemId)
  );

  return {
    promoCartItemsObj,
    promoCartValue: firstListCartValue,
    promoCartQuantity: firstListCartQuantity,
    firstListCartItems,
    firstListCartItemIds,
    firstListCartValue,
    firstListCartQuantity,
    secondListCartItems,
    secondListCartItemIds,
    secondListCartValue,
    secondListCartQuantity,
    progressMade,
    promoSuccessLevel,
    isPromoAchieved,
    bogoItemInCart: promoSuccessLevel === 'success' ? bogoItemInCart : null,
    currentPromoTier,
    secondListItemInCart,
    hasAnchorItemInCart
  };
};

// TODO: check if this is still needed with new header footer
export const updateCartCountInHeader = (newCartModel) => {
  // Need to emit event for cart count in header.
  if (typeof window !== 'undefined' && window.LIFE_CYCLE_EVENT_BUS?.lifeCycle?.events?.hfs?.cartUpdated) {
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('hfs.cartUpdated');
  }

  const cartEvent = document.createEvent('CustomEvent');
  cartEvent.initCustomEvent('cartUpdated', true, false, { detail: { CartModel: newCartModel } });
  document.dispatchEvent(cartEvent);
};
